import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import Text from 'components/shared/DesignSystem/SlabTransferPortal/Text';
import InputClipboard, {
  messages as common,
} from 'components/shared/DesignSystem/SlabTransferPortal/InputClipboard';
import { useCurrentUser } from 'javascripts/hooks';
import CCModal from 'components/shared/DesignSystem/CCModal/CCModal';
import errorLogger from 'helpers/error-logger';
import v1ApiClient from 'helpers/api-client';
import closersAnimation from 'images/lottie/ClosersAnimation.lottie';
import { Steven } from 'helpers/steven';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { translate } from 'javascripts/translations/i18n';

export type ReferralModalProps = {
  onRequestClose: () => void;
};

export type ReferralLinkResponse = {
  referral_link: string;
};

const ImageContainer = styled(DotLottieReact)`
  height: 250px;
  background-color: var(--cc_color_brand_secondary);
  border-radius: var(--cc_size_border_radius_l) var(--cc_size_border_radius_l) 0
    0;
  width: 100%;
  position: absolute;
  margin-left: -32px;
  margin-top: -24px;
`;

const ContentContainer = styled.div`
  margin-top: 260px;
`;

const ReferralLinkContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;

  > div {
    width: 100%;
  }
`;

const ReferralModal = ({ onRequestClose }: ReferralModalProps) => {
  const currentUser = useCurrentUser();
  const [referralLink, setReferralLink] = useState<string | null>(
    currentUser.referral_link,
  );
  const [hasLinkError, setHasLinkError] = useState(false);
  const isLoading = !referralLink && !hasLinkError;

  useEffect(() => {
    const fetchReferralLink = async () => {
      if (!referralLink) {
        try {
          const { referral_link }: ReferralLinkResponse =
            await v1ApiClient.post('/referral_link');
          setReferralLink(referral_link);
        } catch (error) {
          setHasLinkError(true);
          errorLogger({
            error,
            consoleMessage: 'Error creating referral link',
          });
        }
      }
    };
    fetchReferralLink();
  }, [referralLink]);

  return (
    <CCModal
      isOpen
      onRequestClose={onRequestClose}
      secondaryAction={{
        label: translate('referral.modal.programDetails'),
        'data-testid': 'referral-modal__program-details-link',
        onClick: () =>
          window.open('https://companycam.com/referrals', '_blank'),
      }}
    >
      <div />
      <ImageContainer
        src={closersAnimation}
        autoplay
        renderConfig={{ freezeOnOffscreen: false, autoResize: false }}
        loop
      />
      <ContentContainer data-testid="modal_referral">
        {/* @ts-expect-error */}
        <Text family="heading" as="h1" size="xxl" ccMargin="xl none m none">
          {translate('referral.modal.title')}
        </Text>
        {/* @ts-expect-error */}
        <Text as="p" ccMargin="m none">
          {translate('referral.modal.description')}
        </Text>
        <ReferralLinkContainer>
          <InputClipboard
            text={referralLink}
            asyncErrorMessage={
              hasLinkError ? translate('referral.modal.linkError') : ''
            }
            isLoading={isLoading}
            onCopySuccess={() => {
              Steven.elementClicked({
                id: 'referral_link_url',
                content: common.copy.defaultMessage,
                type: 'button',
                action: 'copy_referral_link_url_referral_modal_clicked',
              });
            }}
            data-testid="referral-modal__clipboard-referral-link"
          />
        </ReferralLinkContainer>
      </ContentContainer>
    </CCModal>
  );
};

export default ReferralModal;
